import { Button, Icon, Spinner, Text } from '@lawpilots/design-system'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, IconButton } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
// Emitted as a standalone asset by webpack (see the `pdf.worker` rule in webpack.config.js) and
// stubbed in jest. react-pdf's README reaches the same file via `new URL(..., import.meta.url)`,
// which jest's CommonJS transform cannot parse - this import works in both.
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs'

import { CertificateDto } from '@/services/api/tenant/certificates'
import { useCertificatePreview } from '../../hooks/useCertificatePreview'

// The worker must be configured in the same module that renders <Document>, or module execution
// order can reset it to the default (react-pdf README). `pdfjs-dist` is public-hoisted so we can
// name it at all - see pnpm-workspace.yaml.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

type CertificateViewModalProps = {
  open: boolean
  onCloseAction?: () => void
  certificate: CertificateDto
  onDownload: (certificate: CertificateDto) => void
}

const HeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  padding: '16px 24px',
  borderBottom: '1px solid var(--colors-border-subtle, #e0e0e0)',
}

const FooterStyles = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '16px 24px',
  borderTop: '1px solid var(--colors-border-subtle, #e0e0e0)',
}

// The page sits on a neutral field so the certificate's own white edge reads as a sheet.
const ViewerStyles = {
  height: '70vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  padding: 3,
  backgroundColor: 'var(--colors-surface-canvas, #f4f4f6)',
  '& .react-pdf__Page': { boxShadow: '0 1px 6px rgba(0, 0, 0, 0.16)', borderRadius: '2px' },
  '& .react-pdf__Page__canvas': { display: 'block', maxWidth: '100%', height: 'auto' },
}

const CentredStyles = {
  height: '70vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  backgroundColor: 'var(--colors-surface-canvas, #f4f4f6)',
}

// Rendering width for the page canvas: the MUI `md` dialog (900px) less its gutters.
const PAGE_WIDTH = 820

/**
 * Full-screen preview of one certificate.
 *
 * Renders through react-pdf (pdf.js) rather than the browser's native PDF plugin: pdf.js paints
 * the page onto a canvas, so every browser shows the same thing and the surrounding chrome is
 * ours to style. react-pdf pins `pdfjs-dist` as a direct dependency, which is what stops the two
 * drifting apart - the previous viewer took pdf.js as a `^2 || ^3` peer, the repo moved to v6, and
 * every open threw `renderTextLayer is not a function` (FS-333).
 */
export const CertificateViewModal = ({
  certificate,
  open,
  onCloseAction,
  onDownload,
}: CertificateViewModalProps): JSX.Element => {
  const { t } = useTranslation('general')
  const preview = useCertificatePreview(certificate.url, open)
  const [pageCount, setPageCount] = useState(0)
  const title = t('my_certificates_preview_title', { course: certificate.course_title })

  const handleLoadSuccess = useCallback(({ numPages }: { numPages: number }) => setPageCount(numPages), [])

  const spinner = <Spinner size="lg" label={t('my_certificates_preview_loading')} />
  // One message for a certificate that never arrived, another for one that arrived and cannot be drawn.
  const message = (key: string) => (
    <Text variant="body-reading" color="secondary">
      {t(key)}
    </Text>
  )

  return (
    <Dialog open={open} onClose={onCloseAction} maxWidth="md" fullWidth aria-labelledby="certificate-view-title">
      <Box sx={HeaderStyles}>
        <Text variant="section-title" color="primary" id="certificate-view-title">
          {title}
        </Text>
        <IconButton onClick={onCloseAction} aria-label={t('close')}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ padding: 0 }}>
        {preview.status === 'ready' ? (
          <Box sx={ViewerStyles} data-testid="certificate-preview">
            <Document
              file={preview.blob}
              onLoadSuccess={handleLoadSuccess}
              loading={spinner}
              error={message('my_certificates_preview_render_error')}>
              {Array.from({ length: pageCount }, (_, index) => (
                <Page key={index} pageNumber={index + 1} width={PAGE_WIDTH} />
              ))}
            </Document>
          </Box>
        ) : (
          <Box sx={CentredStyles}>
            {preview.status === 'error' ? message('my_certificates_preview_error') : spinner}
          </Box>
        )}
      </DialogContent>
      <Box sx={FooterStyles}>
        <Button
          variant="secondary"
          icon={<Icon name="download" />}
          onClick={() => onDownload(certificate)}
          disabled={preview.status !== 'ready'}>
          {t('cert_download')}
        </Button>
      </Box>
    </Dialog>
  )
}
